import { canonicalizeHeading } from './canonicalize-heading';

/**
 * Shared note-push plan compiler CORE — the selectors-only heart both callers
 * key on. It matches each section heading to its stored field target by
 * CANONICAL heading (C3), so heading-spelling drift between a note and its
 * template skeleton no longer misses (audit B6). It is deliberately:
 *   - selectors-only — NO note bodies cross this module, so the projects panel
 *     (which imports only this) is STRUCTURALLY unable to leak content;
 *   - policy-free — 0/1/>1 targets classify as missing/mapped/multi-target, but
 *     the escalate-on-miss / fold-on-continue / rematch decisions stay CALLER-side.
 * The warm overlay caller adds bodies via a separate edge mapper (`warm-plan.ts`).
 */

/** A stored section→field row. `uiTargets` are opaque `AxSelector` JSON strings. */
export interface StoredPlacementRow {
  sectionHeading: string;
  uiTargets: string[];
}

/** The classification of one heading against the stored rows. */
export type PlacementMatch =
  /** Exactly one stored target — the opaque `AxSelector` JSON string, verbatim. */
  | { status: 'mapped'; target: string }
  /** No matching row, or a matched row with zero targets. */
  | { status: 'missing' }
  /** A matched row with >1 target — INVALID, never silently reduced to its first. */
  | { status: 'multi_target' };

/** One compiled placement: the heading VERBATIM (never canonicalized) + its target. */
export interface CompiledPlacement {
  heading: string;
  target: string;
}

export interface CompiledPlacements {
  /** One entry per mapped heading, in the given order. Sections MAY share a target. */
  placements: CompiledPlacement[];
  /** Headings with no usable stored target (no matching row, or zero targets). */
  missing: string[];
  /** Headings whose stored row carries >1 target — INVALID, force escalation. */
  multiTarget: string[];
}

/** An index entry: a single confirmed row, or an ambiguous canonical collision. */
type IndexedRow = { kind: 'row'; row: StoredPlacementRow } | { kind: 'ambiguous' };

/** Whether two rows carry the same set of targets (order-insensitive). */
const sameTargets = (a: StoredPlacementRow, b: StoredPlacementRow): boolean => {
  if (a.uiTargets.length !== b.uiTargets.length) return false;
  const bs = [...b.uiTargets].sort();
  return [...a.uiTargets].sort().every((target, i) => target === bs[i]);
};

/**
 * Index stored rows by canonical heading. Two rows that canonicalize the same
 * (e.g. `Plan` and `plan:`) but carry DIFFERENT targets are AMBIGUOUS — GET
 * order would otherwise decide which selector wins and risk writing a section
 * into the wrong field, so the key is poisoned to `multi_target` (escalate).
 * Identical duplicates are idempotent (first-seen wins, they agree).
 */
export const buildStoredRowIndex = (
  rows: readonly StoredPlacementRow[]
): Map<string, IndexedRow> => {
  const index = new Map<string, IndexedRow>();
  for (const row of rows) {
    const key = canonicalizeHeading(row.sectionHeading);
    if (!key) continue;
    const existing = index.get(key);
    if (!existing) index.set(key, { kind: 'row', row });
    else if (existing.kind === 'row' && !sameTargets(existing.row, row))
      index.set(key, { kind: 'ambiguous' });
  }
  return index;
};

/**
 * The confirmed single stored row for a heading, or undefined when there is no
 * matching row OR the canonical key is ambiguous (colliding rows disagree). For
 * display surfaces that read the row's targets directly — an ambiguous key has
 * no confirmed target, so it reads as unmapped (mirrors {@link matchStoredTarget}).
 */
export const storedRowFor = (
  heading: string,
  index: Map<string, IndexedRow>
): StoredPlacementRow | undefined => {
  const entry = index.get(canonicalizeHeading(heading));
  return entry?.kind === 'row' ? entry.row : undefined;
};

/** Classify ONE heading against a prebuilt {@link buildStoredRowIndex}. */
export const matchStoredTarget = (
  heading: string,
  index: Map<string, IndexedRow>
): PlacementMatch => {
  const entry = index.get(canonicalizeHeading(heading));
  if (!entry) return { status: 'missing' };
  if (entry.kind === 'ambiguous') return { status: 'multi_target' };
  const targets = entry.row.uiTargets;
  if (targets.length === 0) return { status: 'missing' };
  if (targets.length > 1) return { status: 'multi_target' };
  // Non-null by the length check; `?? ''` keeps the type honest without a cast.
  return { status: 'mapped', target: targets[0] ?? '' };
};

/**
 * Classify every `heading` (in the given order) against `storedRows`, matched by
 * canonical heading. The caller decides what to do with the buckets.
 */
export const compilePlacements = (
  headings: readonly string[],
  storedRows: readonly StoredPlacementRow[]
): CompiledPlacements => {
  const index = buildStoredRowIndex(storedRows);
  const placements: CompiledPlacement[] = [];
  const missing: string[] = [];
  const multiTarget: string[] = [];
  for (const heading of headings) {
    const match = matchStoredTarget(heading, index);
    if (match.status === 'mapped') placements.push({ heading, target: match.target });
    else if (match.status === 'multi_target') multiTarget.push(heading);
    else missing.push(heading);
  }
  return { placements, missing, multiTarget };
};
